import {
	EntityType,
	type GridEntry,
	type PropertyDefinition,
	type PropertyDefinitionBelongsTo,
	type PropertyDefinitionHas,
} from '../types';

/** Resolved target repositories + swimlane flags for canvas snapping (from axis property ids + direction). */
export type GridAxisSnapFields = {
	repositoryId: string | null | undefined;
	xAxisRepositoryId: string | null;
	yAxisRepositoryId: string | null;
	xAxisSwimlaneEnabled: boolean;
	yAxisSwimlaneEnabled: boolean;
};

export function mergePropertyDefinitions(
	local: PropertyDefinition[],
	remote?: PropertyDefinition[] | null,
): PropertyDefinition[] {
	if (!remote?.length) {
		return local;
	}
	const byId = new Map(local.map((definition) => [definition.id, definition]));
	for (const definition of remote) {
		byId.set(definition.id, definition);
	}
	return Array.from(byId.values());
}

export function targetRepositoryIdForAxisProperty(
	propertyId: string | null | undefined,
	definitions: PropertyDefinition[],
): string | null {
	if (!propertyId) {
		return null;
	}
	const d = definitions.find((p) => p.id === propertyId);
	if (!d) {
		return null;
	}
	if (d.type === EntityType.PropertyDefinitionBelongsTo) {
		return (d as PropertyDefinitionBelongsTo).targetRepositoryId ?? null;
	}
	if (d.type === EntityType.PropertyDefinitionHas) {
		return (d as PropertyDefinitionHas).targetRepositoryId ?? null;
	}
	return null;
}

export function resolveGridAxisSnapFields(
	grid: GridEntry,
	definitions: PropertyDefinition[],
): GridAxisSnapFields {
	const repoId = grid.repositoryId;
	const primaryId = grid.primaryPropertyId;
	const secondaryId = grid.secondaryPropertyId;
	const dir = grid.direction ?? 'vertical';
	let xRepo: string | null;
	let yRepo: string | null;
	let xAxisSwimlaneEnabled: boolean;
	let yAxisSwimlaneEnabled: boolean;
	if (dir === 'horizontal') {
		xRepo = targetRepositoryIdForAxisProperty(secondaryId, definitions);
		yRepo = targetRepositoryIdForAxisProperty(primaryId, definitions);
		xAxisSwimlaneEnabled = false;
		yAxisSwimlaneEnabled = Boolean(primaryId);
	} else {
		xRepo = targetRepositoryIdForAxisProperty(primaryId, definitions);
		yRepo = targetRepositoryIdForAxisProperty(secondaryId, definitions);
		xAxisSwimlaneEnabled = Boolean(primaryId);
		yAxisSwimlaneEnabled = false;
	}
	return {
		repositoryId: repoId,
		xAxisRepositoryId: xRepo,
		yAxisRepositoryId: yRepo,
		xAxisSwimlaneEnabled,
		yAxisSwimlaneEnabled,
	};
}

/** Map primary/secondary + layout to persisted grid fields (`direction` encodes layout when primary is set). */
export function buildGridAxisPropertyFields(args: {
	primaryLayoutVertical: boolean;
	primaryAxisPropertyId: string;
	secondaryAxisPropertyId: string;
}): { primaryPropertyId: string | null; secondaryPropertyId: string | null; direction: string | null } {
	const primary = args.primaryAxisPropertyId || null;
	const secondary = args.secondaryAxisPropertyId || null;
	const hasPrimary = Boolean(primary);
	return {
		primaryPropertyId: primary,
		secondaryPropertyId: secondary,
		direction: hasPrimary ? (args.primaryLayoutVertical ? 'vertical' : 'horizontal') : null,
	};
}

export function formPrimarySecondaryLayoutFromGrid(grid: GridEntry): {
	primaryAxisPropertyId: string;
	secondaryAxisPropertyId: string;
	primaryLayoutVertical: boolean;
} {
	const dir = grid.direction ?? 'vertical';
	return {
		primaryLayoutVertical: dir !== 'horizontal',
		primaryAxisPropertyId: grid.primaryPropertyId ?? '',
		secondaryAxisPropertyId: grid.secondaryPropertyId ?? '',
	};
}
